import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Package, AlertTriangle, ArrowRight, Plus, TrendingDown, ShoppingCart,
  Send, ClipboardList, Boxes, RotateCcw, Wrench,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * Spare Parts landing page.
 * 6 KPI cards + Needs Attention panel + 5 quick actions.
 */
export default function SpareOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/spares/parts/overview")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const kpis = data?.kpis || {};
  const attention = data?.attention || [];

  return (
    <div className="space-y-5" data-testid="spare-overview-page">
      {/* Header */}
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[24px] md:text-[28px] font-bold text-[#1d3557] leading-tight">Spare Parts</h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            What&apos;s available, what&apos;s required, what needs action — one view.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <QuickAction icon={ShoppingCart} label="Counter Sale" to="/spare-parts/counter-sales" testId="spare-qa-counter" />
          <QuickAction icon={Package}      label="Receive Stock" to="/spare-parts/stock?receive=1" testId="spare-qa-receive" />
          <QuickAction icon={Wrench}       label="Purchases"   to="/spare-parts/purchases"        testId="spare-qa-purchases" />
          <Link
            to="/spare-parts/stock/new"
            data-testid="spare-qa-add-part"
            className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5"
          >
            <Plus size={13} /> Add Part
          </Link>
        </div>
      </header>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi icon={Boxes} label="In Stock" value={kpis.in_stock} tone="green"
             onClick={() => nav("/spare-parts/stock?view=in_stock")} testId="spare-kpi-in-stock" loading={loading} />
        <Kpi icon={TrendingDown} label="Low Stock" value={kpis.low_stock} tone="amber"
             onClick={() => nav("/spare-parts/stock?view=low_stock")} testId="spare-kpi-low-stock" loading={loading} />
        <Kpi icon={AlertTriangle} label="Out of Stock" value={kpis.out_of_stock} tone="red"
             onClick={() => nav("/spare-parts/stock?view=out_of_stock")} testId="spare-kpi-out-of-stock" loading={loading} />
      </div>

      {/* Needs Attention */}
      <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="spare-needs-attention">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-[14px] font-bold text-[#1d3557]">Needs Attention</h3>
            <p className="text-[11.5px] text-[#667085]">Top items that block workshop flow right now.</p>
          </div>
          <Link
            to="/spare-parts/reorder"
            className="text-[11.5px] font-semibold text-[#457b9d] hover:text-[#e63946] inline-flex items-center gap-1"
            data-testid="spare-attention-view-all"
          >
            View reorder alerts <ArrowRight size={11} />
          </Link>
        </div>
        {loading ? (
          <div className="text-[13px] text-[#667085] py-6 text-center">Loading…</div>
        ) : attention.length === 0 ? (
          <div className="text-[13px] text-[#667085] py-6 text-center">Everything looks healthy. Nothing needs attention right now.</div>
        ) : (
          <ul className="divide-y divide-[#F1FAEE]">
            {attention.map((a) => (
              <li key={a.part_id} className="py-2.5 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-md grid place-items-center ${
                  a.kind === "out_of_stock" ? "bg-[#e63946]/10 text-[#e63946]" : "bg-[#FEF0C7] text-[#B54708]"
                }`}>
                  {a.kind === "out_of_stock" ? <AlertTriangle size={14} /> : <TrendingDown size={14} />}
                </div>
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/spare-parts/stock/${a.part_id}`}
                    className="text-[13px] font-semibold text-[#1d3557] hover:text-[#e63946]"
                    data-testid={`spare-attention-part-${a.part_id.slice(0,8)}`}
                  >
                    {a.name}
                  </Link>
                  <div className="text-[11.5px] text-[#667085]">{a.reason}</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-[#667085] uppercase tracking-wider font-semibold">Available</div>
                  <div className="text-[15px] font-bold font-mono text-[#1d3557] tabular-nums">{a.available}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone = "steel", onClick, testId, loading }) {
  const toneMap = {
    steel: "bg-white border-[#E4E7EC] text-[#1d3557]",
    green: "bg-[#F1FAEE] border-[#12B76A]/20 text-[#0F9256]",
    amber: "bg-[#FEF0C7] border-[#F79009]/25 text-[#B54708]",
    red:   "bg-[#e63946]/8 border-[#e63946]/25 text-[#e63946]",
    blue:  "bg-[#a8dadc]/20 border-[#a8dadc] text-[#1d3557]",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-xl border p-3.5 hover:shadow-md transition-shadow ${toneMap[tone]}`}
    >
      <div className="flex items-start justify-between">
        <div className="w-8 h-8 rounded-md bg-white/60 grid place-items-center">
          <Icon size={15} />
        </div>
      </div>
      <div className="text-[11px] uppercase tracking-wider font-semibold mt-2 text-[#667085]">{label}</div>
      <div className="text-[24px] font-bold text-[#1d3557] font-mono tabular-nums mt-0.5">
        {loading ? "—" : (value ?? 0)}
      </div>
    </button>
  );
}

function QuickAction({ icon: Icon, label, to, testId, disabled }) {
  return (
    <Link
      to={disabled ? "#" : to}
      data-testid={testId}
      onClick={(e) => disabled && e.preventDefault()}
      className={`h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold inline-flex items-center gap-1.5 ${
        disabled ? "text-[#98A2B3] bg-[#F7F9FC] cursor-not-allowed" : "text-[#1d3557] hover:bg-[#F1FAEE]"
      }`}
      title={disabled ? "Available in iteration 18" : label}
    >
      <Icon size={13} /> {label}
    </Link>
  );
}
